import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import '../alert/Alert.css';
import './AppSupport.css';
import { v4 as uuidv4 } from 'uuid';
import { userAppData } from '../MainApp.js';
import useS3FileUpload from '../hooks/useS3FileUpload';
import { FileDropzone } from '../common/formComponents';
import { SEC_DataApiPost } from '../func';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const REQUEST_TYPE_OPTIONS = [
    { value: 'Support Request', label: 'Support Request' },
    { value: 'Bug Report', label: 'Bug Report' },
    { value: 'Feature Request', label: 'Feature Request' },
    { value: 'Improvement Suggestion', label: 'Improvement Suggestion' },
    { value: 'Training Request', label: 'Training Request' }
];

const REQUEST_TYPE_GUIDANCE = {
    'Support Request': {
        title: 'Support Request guidance',
        helperLines: [
            'What are you trying to do?',
            'What is stopping you?',
            'When did this start happening?'
        ],
        placeholder: "Describe what you're trying to do, what's stopping you, and when it started..."
    },
    'Bug Report': {
        title: 'Bug Report guidance',
        helperLines: [
            'What did you expect to happen?',
            'What actually happened?',
            'Steps to reproduce (1, 2, 3)',
            'Any error message (copy and paste)',
            'Device/browser (if known)'
        ],
        placeholder: 'Expected: ...\nActual: ...\nSteps to reproduce:\n1)\n2)\n3)\nError message: ...\nDevice/browser: ...'
    },
    'Feature Request': {
        title: 'Feature Request guidance',
        helperLines: [
            'What problem would this solve for you?',
            'Who would use it?',
            'What would "good" look like?'
        ],
        placeholder: 'Describe the problem, who it helps, and what success looks like...'
    },
    'Improvement Suggestion': {
        title: 'Improvement Suggestion guidance',
        helperLines: [
            'What feels awkward or slow?',
            'What small change would improve it?',
            'Any example screenshot or context (optional)'
        ],
        placeholder: 'Tell us what feels awkward/slow and what small change would improve it...'
    },
    'Training Request': {
        title: 'Training Request guidance',
        helperLines: [
            'What do you need help learning?',
            'Which screen/module is it in?',
            'Would a quick guide, video, or in-app tips be best?'
        ],
        placeholder: 'Describe what you need help learning and what kind of training would suit you...'
    }
};

/**
 * Create form for App Support requests (UI-only draft).
 */
export default function AppSupportCreate() {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [requestType, setRequestType] = useState(null);
    const [details, setDetails] = useState('');
    const [files, setFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('');
    const { uploadFiles } = useS3FileUpload('appSupport');
    const toastConfig = { autoClose: 4000, position: 'top-right' };

    const guidance = useMemo(() => {
        if (!requestType?.value) {
            return null;
        }
        return REQUEST_TYPE_GUIDANCE[requestType.value] || null;
    }, [requestType]);

    const placeholderText = guidance?.placeholder || 'Select a request type to see suggested prompts...';
    const isFormValid = title.trim().length > 0 && requestType?.value && details.trim().length > 0;

    // Reset the UI-only success message when inputs change.
    const clearSuccessMessage = () => {};

    // Handle UI-only submit and log the draft payload for now.
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!isFormValid || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('Uploading files...');

        const payload = {
            title: title.trim(),
            type: requestType?.value || '',
            details: details.trim(),
            files: [],
            uploadGroupId: null
        };

        try {
            if (files.length > 0) {
                const uploadGroupId = uuidv4();
                const uploaded = await uploadFiles(files, userAppData.cogId, { uploadGroupId });
                payload.files = uploaded;
                payload.uploadGroupId = uploadGroupId;
            }

            setSubmitStatus('Submitting request...');
            const response = await SEC_DataApiPost('appsupport', JSON.stringify(payload), {}, () => {
                throw new Error('App Support submission failed');
            });
            const caseId = response?.caseId;
            const requestId = response?.requestId;
            const displayId = caseId || requestId;
            if (!displayId || displayId === 'error') {
                throw new Error('Invalid or missing request ID');
            }

            toast.success(`✅ App Support request submitted (${displayId})`, toastConfig);
            setTitle('');
            setRequestType(null);
            setDetails('');
            setFiles([]);
        } catch (error) {
            console.error('App Support submission error:', error);
            toast.error('❌ App Support request failed. Please try again.', toastConfig);
        } finally {
            setIsSubmitting(false);
            setSubmitStatus('');
        }
    };

    return (
        <div className="alert-form-shell">
            <div className="alert-form-panel">
                <div className="appsupport-form-actions">
                    <button
                        type="button"
                        className="alert-single-back"
                        onClick={() => navigate('/app/support/requests')}
                    >
                        Back to App Support
                    </button>
                </div>
                <div className="alert-form-header">
                    <p className="alert-form-kicker">New Request</p>
                    <h1 className="alert-form-title">App Support</h1>
                    <p className="alert-form-subtitle">
                        Share what you need so we can route your request quickly.
                    </p>
                </div>

                <form className="alert-form" onSubmit={handleSubmit}>
                    <div className="alert-form-grid">
                        <label className="alert-form-field">
                            <span className="alert-form-label">Request Title</span>
                            <input
                                type="text"
                                className="alert-form-input"
                                value={title}
                                onChange={(event) => {
                                    clearSuccessMessage();
                                    setTitle(event.target.value);
                                }}
                                placeholder="Short summary of your request"
                            />
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Request Type</span>
                            <Select
                                className="alert-form-select"
                                classNamePrefix="alert-select"
                                options={REQUEST_TYPE_OPTIONS}
                                value={requestType}
                                onChange={(selectedOption) => {
                                    clearSuccessMessage();
                                    setRequestType(selectedOption);
                                }}
                                placeholder="Select a request type"
                            />
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Details</span>
                            <div className="alert-form-guidance">
                                <p className="alert-form-guidance-title">
                                    {guidance?.title || 'Details guidance'}
                                </p>
                                {guidance ? (
                                    <ul className="alert-form-guidance-list">
                                        {guidance.helperLines.map((line) => (
                                            <li key={line} className="alert-form-guidance-item">
                                                {line}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="alert-form-helper">
                                        Select a request type to see suggested prompts.
                                    </p>
                                )}
                            </div>
                            <textarea
                                className="alert-form-input alert-form-textarea"
                                value={details}
                                onChange={(event) => {
                                    clearSuccessMessage();
                                    setDetails(event.target.value);
                                }}
                                placeholder={placeholderText}
                            />
                        </label>

                        <div className="alert-form-field">
                            <span className="alert-form-label">Attachments</span>
                            <FileDropzone
                                filesState={files}
                                fileStateSet={(nextFiles) => {
                                    clearSuccessMessage();
                                    setFiles(nextFiles);
                                }}
                                maxFiles={10}
                                maxTotalSizeMB={50}
                                enableClipboard
                                fullWidth
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="alert-create-button alert-form-submit"
                        disabled={!isFormValid || isSubmitting}
                    >
                        {submitStatus || 'Submit Request'}
                    </button>
                </form>
            </div>
        </div>
    );
}

